import { useCallback, useState } from 'react';

import type { ComposerBackgroundMode } from '../useComposerBackground';

interface UseComposerBackgroundControlsOptions {
  backgroundMode: ComposerBackgroundMode;
  customColor: string;
  setCustomColor: (hex: string) => void;
  setPresetBackground: (mode: 'default' | 'black' | 'white' | 'gray') => void;
}

function useComposerBackgroundControls({
  backgroundMode,
  customColor,
  setCustomColor,
  setPresetBackground
}: UseComposerBackgroundControlsOptions) {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorPickerPrevMode, setColorPickerPrevMode] =
    useState<ComposerBackgroundMode>('default');
  const [colorPickerPrevColor, setColorPickerPrevColor] = useState('#ffffff');

  const handleOpenColorPicker = useCallback(() => {
    setColorPickerPrevMode(backgroundMode);
    setColorPickerPrevColor(customColor);
    setColorPickerOpen(true);
  }, [backgroundMode, customColor]);

  const handleConfirmColorPicker = useCallback(
    (hex: string) => {
      setCustomColor(hex);
      setColorPickerOpen(false);
    },
    [setCustomColor]
  );

  const handleCancelColorPicker = useCallback(() => {
    if (colorPickerPrevMode !== 'custom') {
      setPresetBackground(
        colorPickerPrevMode as 'default' | 'black' | 'white' | 'gray'
      );
    } else {
      setCustomColor(colorPickerPrevColor);
    }
    setColorPickerOpen(false);
  }, [
    colorPickerPrevMode,
    colorPickerPrevColor,
    setCustomColor,
    setPresetBackground
  ]);

  return {
    colorPickerOpen,
    colorPickerPrevColor,
    handleOpenColorPicker,
    handleConfirmColorPicker,
    handleCancelColorPicker
  };
}

export default useComposerBackgroundControls;
